import { groupBy } from "@/helpers/groupByHelper";
import {
	BookingAddressResponse,
	BookingCharityResourceResponse,
	BookingCharityResponse,
	BookingDepartureResponse,
	BookingPartResponse,
	BookingPassengerResponse,
	BookingResourceResponse,
	BookingResponse
} from "../api/BookingResponse";
import { PassengerResponse } from "../api/PassengerDetailsResponse";
import { DirectionEnum, ModeOfTravelEnum, PassengerTypeEnum, TicketTypeEnum } from "../enums";
import {
	AddressInformationState,
	BookingPartState,
	BookingState,
	DepartureState,
	PassengerState,
	ResourceState,
	TermState,
	TicketTypeState
} from "./store/BookingState";
import { CharityResource, CharityState } from "./store/CharityState";

export class Booking implements BookingState {
	amendmentMode: boolean;
	bookingNumber: number;
	modeOfTravel: ModeOfTravelEnum;
	tripType: DirectionEnum;
	leadPassengerId: string;
	addressInformation: AddressInformationState;
	terms: TermState[];
	outward: BookingPartState;
	return: BookingPartState;
	charity: BookingCharity;
	sendConfirmation: boolean;
	sendConfirmationCopy: boolean;
	
	constructor(resp: BookingResponse) {
		this.amendmentMode = true;
		this.bookingNumber = resp.bookingNumber;
		this.modeOfTravel = resp.modeOfTravel;
		this.tripType = resp.bookingParts.length > 1 ? DirectionEnum.Return : DirectionEnum.Outward;
		this.leadPassengerId = resp.leadPassengerId;
		this.addressInformation = new Address(resp.addressInformation);
		this.terms = [] as TermState[];
		this.sendConfirmation = false;
		this.sendConfirmationCopy = true;

		if (resp.bookingParts.length > 1) {
			this.outward = new BookingPart(resp.bookingParts[0], resp.modeOfTravel, true, resp.bookingParts[1].route);
			this.return = new BookingPart(resp.bookingParts[1], resp.modeOfTravel, false, resp.bookingParts[0].route);
		}
		else {
			this.outward = new BookingPart(resp.bookingParts[0], resp.modeOfTravel, true);
			this.return = {} as BookingPartState;
		}

		this.charity = new BookingCharity(resp.bookingCharity);
	}
}

export class BookingPart implements BookingPartState {
	isLocked: boolean;
	from: string;
	to: string;
	route: string;
	date: Date;
	departure: Departure;
	passengers: Passenger[];
	resources: Resource[];

	constructor(resp: BookingPartResponse, modeOfTravel: ModeOfTravelEnum, outward: boolean, route?: string) {
		this.isLocked = resp.departure.isLocked;
		this.from = modeOfTravel === ModeOfTravelEnum.FlyAndSail ? resp.route : resp.route.substring(0, 3);
		
		if (route) {
			this.to = modeOfTravel === ModeOfTravelEnum.FlyAndSail ? route : resp.route.substring(3, 6);
		}
		else {
			this.to = resp.route.substring(3, 6);
		}

		this.route = resp.route;
		this.date = resp.departureDateTime;
		this.departure = new Departure(resp.departure);
		this.passengers = [];
		this.resources = [];

		if (outward) {
			//grouping passengers since we need to set
			//id from 1 on each passenger type
			const groupedPassengers = groupBy(resp.passengers, "passengerType");
			for (const key in groupedPassengers) {
				this.passengers = this.passengers.concat((groupedPassengers[key] as BookingPassengerResponse[])
					.map((r, index) => new Passenger(r, index)));
			}

			//grouping resources since we need to set
			//id from 1 on each resource type
			const groupedResources = groupBy(resp.resources, "code");
			for (const key in groupedResources) {
				this.resources = this.resources.concat((groupedResources[key] as BookingResourceResponse[])
					.map((r, index) => new Resource(r, index)));
			}
		}
		else {
			//grouping resources since we need to set
			//id from 1 on each resource type
			//for return we only want to save bus transfers 
			//in the return bookingpart state since we only handle
			//that resource unique for each departure
			const transferResources = resp.resources?.filter(x => x.code === "BUS");
			const groupedResources = groupBy(transferResources, "code");
			for (const key in groupedResources) {
				this.resources = this.resources.concat((groupedResources[key] as BookingResourceResponse[])
							.map((r, index) => new Resource(r, index)));
			}		
		}
	}
}

export class Address implements AddressInformationState {
	postCode: string;
	city: string;
	countryCode: string;
	address: string;
	email: string;
	mobilePhone: string;

	constructor(resp: BookingAddressResponse) {
		this.postCode = resp.postCode;
		this.city = resp.city;
		this.countryCode = resp.countryCode;
		this.address = resp.address;
		this.mobilePhone = resp.mobilePhone;
		this.email = resp.email;
	}
}

export class Passenger implements PassengerState {
	type: PassengerTypeEnum;
	id: number;
	nameListId?: string;
	title?: string;
	firstName?: string;
	lastName?: string;
	dateOfBirth?: string;

	constructor(resp: PassengerResponse, index: number) {
		this.type = resp.passengerType;
		this.id = index + 1;
		this.firstName = resp.firstName;
		this.lastName = resp.lastName;
		this.dateOfBirth = resp.dateOfBirth;
	}
}

export class Departure implements DepartureState {
	id: string;
	identifier: string;
	ticketType: TicketTypeState;
	type: TicketTypeEnum;

	constructor(resp: BookingDepartureResponse) {
		this.id = `${resp.route}|${resp.departureDateTime}`;
		this.identifier = resp.identifier ?? "";
		this.type = resp.type;
		this.ticketType = {
			id: `${this.id}|${TicketTypeEnum[this.type]}`
		} as TicketTypeState;
	}
}

export class Resource implements ResourceState {
	identifier: string;
	code: string;
	id: number;
	route: string;

	constructor(resp: BookingResourceResponse, index: number) {
		this.identifier = resp.identifier ?? "";
		this.code = resp.code ?? "";
		this.route = resp.route ?? "";
		this.id = index + 1;
	}
}

export class BookingCharity implements CharityState {
	noDonationResources: string[];
	resources: CharityResource[];

	constructor(resp: BookingCharityResponse) {
		this.noDonationResources = resp.noDonationResources;
		this.resources = resp.resources.map(c => new BookingCharityResource(c));
	}
}

export class BookingCharityResource implements CharityResource {
	prefix: string;
	resourceCode: string;
	identifier: string;
	giftAid: boolean;

	constructor(resp: BookingCharityResourceResponse) {
		this.identifier = "";
		this.prefix = resp.prefix;
		this.resourceCode = resp.resourceCode;
		this.giftAid = resp.giftAid;
	}
}